/**
 * Tenancy-safe Franchise Data Hub context.
 *
 * Combines the global waffle flag with org-scoped FranConnect project
 * resolution so other tenants never see JMFS nav or project IDs.
 */
import api from './api';
import featureFlags from './featureFlags';

const state = {
  context: null,
  loaded: false,
  loadingPromise: null,
};

function emptyContext() {
  return {
    visible: false,
    hub_enabled: false,
    feature_flag: false,
    project_id: null,
    organization_id: null,
    organization_name: null,
    project_name: null,
    allowed_project_ids: [],
    projects: [],
  };
}

export default {
  async load(force = false) {
    if (state.loaded && !force) return state.context;
    if (state.loadingPromise && !force) return state.loadingPromise;

    const request = Promise.all([
      featureFlags.load(force).catch(() => {}),
      api.get('/franconnect/franchise-hub-context/'),
    ])
      .then(([, response]) => {
        state.context = response.data?.data || emptyContext();
        state.loaded = true;
        return state.context;
      })
      .catch(async (err) => {
        const status = err?.response?.status;
        // Token refresh race: retry once instead of sticking on "not configured".
        if (status === 401 && !force) {
          await new Promise((resolve) => setTimeout(resolve, 400));
          return this.load(true);
        }
        console.warn('franchiseHubContext load failed:', err?.message || err);
        if (!state.loaded) {
          state.context = emptyContext();
        }
        return state.context || emptyContext();
      })
      .finally(() => {
        if (state.loadingPromise === request) {
          state.loadingPromise = null;
        }
      });

    state.loadingPromise = request;
    return request;
  },

  getContext() {
    return state.context || emptyContext();
  },

  isHubEnabled() {
    const ctx = this.getContext();
    return Boolean(ctx.hub_enabled || (ctx.visible && featureFlags.isEnabled('jmfs_franchise_data_hub')));
  },

  getDefaultProjectId() {
    return this.getContext().project_id;
  },

  getAllowedProjectIds() {
    return this.getContext().allowed_project_ids || [];
  },

  canAccessProject(projectId) {
    const pid = Number(projectId);
    if (!Number.isFinite(pid)) return false;
    return this.getAllowedProjectIds().includes(pid);
  },

  getHubPath(projectId = null) {
    const pid = projectId || this.getDefaultProjectId();
    if (!pid) return null;
    return `/projects/${pid}/franchise-data`;
  },

  reset() {
    state.context = null;
    state.loaded = false;
    state.loadingPromise = null;
  },
};
